import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { 
  ChefHat, Plus, X, Clock, Users, Star, BookOpen, 
  Leaf, Heart, Wheat, Apple, MessageCircle, Send,
  Sparkles, Filter, Search, Bot, Video, Loader2, Download
} from 'lucide-react';
import { useTranslation } from 'react-i18next';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const DietRecipesHub = ({ token }) => {
  // Diet filter state
  const { t } = useTranslation();
  const [activeDiet, setActiveDiet] = useState('all');
  const [activeTab, setActiveTab] = useState('recipes'); // recipes, generate, nutritionist
  
  // Recipe state
  const [recipeRequest, setRecipeRequest] = useState({
    ingredients: [],
    dietary_restrictions: [],
    cuisine_type: 'any',
    serving_size: 4,
    cooking_time: 'medium'
  });
  const [generatedRecipes, setGeneratedRecipes] = useState([]);
  const [filteredRecipes, setFilteredRecipes] = useState([]);
  const [currentRecipe, setCurrentRecipe] = useState(null);
  const [loading, setLoading] = useState(false);
  const [ingredientInput, setIngredientInput] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  
  // AI Nutritionist state
  const [chatMessages, setChatMessages] = useState([]);
  const [chatInput, setChatInput] = useState('');
  const [chatLoading, setChatLoading] = useState(false);
  const chatEndRef = useRef(null);

  // Recipe video state
  const [videoGenerating, setVideoGenerating] = useState(false);
  const [videoJobId, setVideoJobId] = useState(null);
  const [videoStatus, setVideoStatus] = useState(null);

  // Diet types with icons and colors
  const dietTypes = [
    { id: 'all', name: 'All Recipes', icon: BookOpen, color: 'bg-gray-500', lightColor: 'bg-gray-100' },
    { id: 'organic', name: 'Organic', icon: Leaf, color: 'bg-green-500', lightColor: 'bg-green-100' },
    { id: 'vegan', name: 'Vegan', icon: Apple, color: 'bg-emerald-500', lightColor: 'bg-emerald-100' },
    { id: 'gluten-free', name: 'Gluten-Free', icon: Wheat, color: 'bg-amber-500', lightColor: 'bg-amber-100' },
    { id: 'healthy', name: 'Heart Healthy', icon: Heart, color: 'bg-red-500', lightColor: 'bg-red-100' },
  ];

  const cuisineOptions = [
    'any', 'Italian', 'Asian', 'Mexican', 'Mediterranean', 'Indian', 
    'American', 'Thai', 'Japanese', 'French', 'Middle Eastern'
  ];

  useEffect(() => {
    fetchGeneratedRecipes();
    loadChatHistory();
  }, []);

  useEffect(() => {
    filterRecipes();
  }, [activeDiet, generatedRecipes, searchQuery]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [chatMessages]);

  // Poll video job status
  useEffect(() => {
    if (!videoJobId) return;
    const interval = setInterval(async () => {
      try {
        const res = await axios.get(`${API}/video/status/${videoJobId}`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setVideoStatus(res.data);
        if (res.data.status === 'completed' || res.data.status === 'failed') {
          setVideoGenerating(false);
          clearInterval(interval);
        }
      } catch { /* ignore */ }
    }, 5000);
    return () => clearInterval(interval);
  }, [videoJobId]);

  const fetchGeneratedRecipes = async () => {
    try {
      const response = await axios.get(`${API}/recipes/generated`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setGeneratedRecipes(response.data.generated_recipes || response.data.recipes || []);
    } catch (error) {
      console.error('Error fetching recipes:', error);
    }
  };

  const loadChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/chat/history`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const history = response.data.messages || [];
      const formattedHistory = history.flatMap(msg => [
        { role: 'user', content: msg.message },
        { role: 'assistant', content: msg.response }
      ]);
      setChatMessages(formattedHistory.slice(-20)); // Last 20 messages
    } catch (error) {
      console.error('Error loading chat history:', error);
    }
  };

  const filterRecipes = () => {
    let filtered = [...generatedRecipes];
    
    // Filter by diet type
    if (activeDiet !== 'all') {
      filtered = filtered.filter(recipe => {
        const restrictions = recipe.dietary_restrictions || [];
        const tags = recipe.tags || [];
        const allTags = [...restrictions, ...tags].map(t => t.toLowerCase());
        
        if (activeDiet === 'vegan') {
          return allTags.some(t => t.includes('vegan'));
        } else if (activeDiet === 'gluten-free') {
          return allTags.some(t => t.includes('gluten'));
        } else if (activeDiet === 'organic') {
          return allTags.some(t => t.includes('organic') || t.includes('natural'));
        } else if (activeDiet === 'healthy') {
          return allTags.some(t => t.includes('healthy') || t.includes('low-fat') || t.includes('heart'));
        }
        return true;
      });
    }
    
    // Filter by search
    if (searchQuery) {
      filtered = filtered.filter(recipe => 
        recipe.title?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        recipe.name?.toLowerCase().includes(searchQuery.toLowerCase()) ||
        recipe.ingredients?.some(i => i.toLowerCase().includes(searchQuery.toLowerCase()))
      );
    }
    
    setFilteredRecipes(filtered);
  };

  const addIngredient = () => {
    if (ingredientInput.trim() && !recipeRequest.ingredients.includes(ingredientInput.trim())) {
      setRecipeRequest({
        ...recipeRequest,
        ingredients: [...recipeRequest.ingredients, ingredientInput.trim()]
      });
      setIngredientInput('');
    }
  };

  const removeIngredient = (ingredient) => {
    setRecipeRequest({
      ...recipeRequest,
      ingredients: recipeRequest.ingredients.filter(item => item !== ingredient)
    });
  };

  const generateRecipeForDiet = async (dietType = null) => {
    setLoading(true);
    try {
      const restrictions = [...recipeRequest.dietary_restrictions];
      
      // Add diet-specific restrictions
      if (dietType || activeDiet !== 'all') {
        const diet = dietType || activeDiet;
        if (diet === 'vegan' && !restrictions.includes('Vegan')) {
          restrictions.push('Vegan');
        } else if (diet === 'gluten-free' && !restrictions.includes('Gluten-Free')) {
          restrictions.push('Gluten-Free');
        } else if (diet === 'organic') {
          restrictions.push('Organic', 'Natural Ingredients');
        } else if (diet === 'healthy') {
          restrictions.push('Low-Fat', 'Heart-Healthy');
        }
      }

      const response = await axios.post(`${API}/recipes/generate`, {
        ...recipeRequest,
        dietary_restrictions: restrictions,
        ingredients: recipeRequest.ingredients.length > 0 ? recipeRequest.ingredients : ['any healthy ingredients']
      }, {
        headers: { Authorization: `Bearer ${token}` }
      });
      
      setCurrentRecipe(response.data.recipe);
      fetchGeneratedRecipes();
      setActiveTab('recipes');
    } catch (error) {
      console.error('Error generating recipe:', error);
      alert(error.response?.data?.detail || 'Failed to generate recipe');
    } finally {
      setLoading(false);
    }
  };

  const sendChatMessage = async () => {
    if (!chatInput.trim() || chatLoading) return;
    
    const userMessage = chatInput.trim();
    setChatInput('');
    setChatMessages(prev => [...prev, { role: 'user', content: userMessage }]);
    setChatLoading(true);
    
    try {
      const response = await axios.post(`${API}/chat/ask`, 
        { message: userMessage },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      
      setChatMessages(prev => [...prev, { role: 'assistant', content: response.data.response }]);
    } catch (error) {
      console.error('Error sending message:', error);
      setChatMessages(prev => [...prev, { 
        role: 'assistant', 
        content: 'Sorry, I encountered an error. Please try again.' 
      }]);
    } finally {
      setChatLoading(false);
    }
  };

  const askNutritionistForRecipe = (dietType) => {
    const questions = {
      'vegan': "Can you suggest a delicious and nutritious vegan recipe for me based on my health goals?",
      'gluten-free': "I need a gluten-free recipe recommendation. What would you suggest for my diet?",
      'organic': "Can you recommend a recipe using organic, whole food ingredients?",
      'healthy': "What's a heart-healthy recipe you'd recommend for me today?"
    };
    
    setChatInput(questions[dietType] || "Can you suggest a healthy recipe for me?");
    setActiveTab('nutritionist');
  };

  const generateRecipeVideo = async (recipe) => {
    setVideoGenerating(true);
    setVideoStatus(null);
    setVideoJobId(null);

    const recipeName = recipe.title || recipe.name || 'Recipe';
    const ingredients = (recipe.ingredients || []).slice(0, 5).join(', ');
    const steps = (recipe.instructions || []).slice(0, 3).join('. ');
    const dietTags = (recipe.dietary_restrictions || []).join(', ');

    const prompt = `A beautiful step-by-step cooking tutorial video for "${recipeName}". ${dietTags ? `This is a ${dietTags} recipe. ` : ''}Show cinematic close-up shots of: preparing ingredients (${ingredients}), the cooking process (${steps}), and the final beautifully plated dish. Warm kitchen lighting, modern kitchen, professional food styling, appetizing colors.`;

    try {
      const res = await axios.post(`${API}/video/generate`, {
        prompt,
        size: '1280x720',
        duration: 8
      }, { headers: { Authorization: `Bearer ${token}` } });
      setVideoJobId(res.data.video_id);
      setVideoStatus({ status: 'processing' });
    } catch (err) {
      const d = err.response?.data?.detail;
      setVideoStatus({ status: 'failed', error: typeof d === 'string' ? d : 'Failed to start video generation' });
      setVideoGenerating(false);
    }
  };

  const quickChatPrompts = [
    "Suggest a vegan breakfast",
    "Gluten-free dinner ideas",
    "Organic meal prep tips",
    "Low-calorie snack recipes",
    "High-protein vegan meals"
  ];

  return (
    <div className="space-y-6 pb-8">
      {/* Header */}
      <div className="bg-gradient-to-r from-green-500 to-emerald-600 p-6 rounded-xl shadow-lg text-white">
        <h2 className="text-xl md:text-2xl font-bold mb-2 flex items-center gap-3">
          <ChefHat size={28} />
          Diet & Recipe Hub
        </h2>
        <p className="text-sm md:text-base opacity-90">
          Discover recipes tailored to your dietary needs with AI-powered recommendations
        </p>
      </div>

      {/* Diet Type Quick Filters */}
      <div className="bg-white dark:bg-gray-800 p-4 rounded-xl shadow-lg">
        <div className="flex items-center gap-2 mb-4">
          <Filter size={18} className="text-[#FF6600]" />
          <span className="font-semibold text-sm">Filter by Diet Type:</span>
        </div>
        <div className="flex flex-wrap gap-2">
          {dietTypes.map((diet) => {
            const Icon = diet.icon;
            const isActive = activeDiet === diet.id;
            return (
              <button
                key={diet.id}
                onClick={() => setActiveDiet(diet.id)}
                className={`flex items-center gap-2 px-3 py-2 rounded-full transition-all text-sm ${
                  isActive 
                    ? `${diet.color} text-white shadow-lg` 
                    : `${diet.lightColor} text-gray-700 dark:text-gray-300 hover:shadow-md`
                }`}
                data-testid={`diet-filter-${diet.id}`}
              >
                <Icon size={16} />
                <span className="font-medium">{diet.name}</span>
              </button>
            );
          })}
        </div>
      </div>

      {/* Main Navigation Tabs */}
      <div className="flex flex-wrap gap-2 border-b border-gray-200 dark:border-gray-700 pb-2">
        <button
          onClick={() => setActiveTab('recipes')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-all text-sm ${
            activeTab === 'recipes' 
              ? 'bg-[#FF6600] text-white' 
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
          }`}
          data-testid="tab-recipes"
        >
          <BookOpen size={18} />
          <span className="hidden sm:inline">Browse</span> ({filteredRecipes.length})
        </button>
        <button
          onClick={() => setActiveTab('generate')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-all text-sm ${
            activeTab === 'generate' 
              ? 'bg-[#FF6600] text-white' 
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
          }`}
          data-testid="tab-generate"
        >
          <Sparkles size={18} />
          <span className="hidden sm:inline">AI</span> Generate
        </button>
        <button
          onClick={() => setActiveTab('nutritionist')}
          className={`flex items-center gap-2 px-4 py-2 rounded-t-lg font-medium transition-all text-sm ${
            activeTab === 'nutritionist' 
              ? 'bg-[#FF6600] text-white' 
              : 'bg-gray-100 dark:bg-gray-700 text-gray-600 dark:text-gray-300 hover:bg-gray-200'
          }`}
          data-testid="tab-nutritionist"
        >
          <Bot size={18} />
          <span className="hidden sm:inline">AI</span> Nutritionist
        </button>
      </div>

      {/* Browse Recipes Tab */}
      {activeTab === 'recipes' && (
        <div className="space-y-4">
          {/* Search Bar */}
          <div className="relative">
            <Search size={18} className="absolute left-4 top-1/2 transform -translate-y-1/2 text-gray-400" />
            <input
              type="text"
              placeholder="Search recipes by name or ingredient..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-12 pr-4 py-3 rounded-xl border border-gray-200 dark:border-gray-700 focus:border-[#FF6600] focus:ring-2 focus:ring-[#FF6600]/20 text-sm"
              data-testid="recipe-search"
            />
          </div>

          {/* Quick Generate Buttons */}
          <div className="bg-gradient-to-r from-amber-50 to-orange-50 dark:from-amber-900/20 dark:to-orange-900/20 p-4 rounded-xl border border-amber-200 dark:border-amber-800">
            <div className="flex items-center gap-2 mb-3">
              <Sparkles size={18} className="text-amber-500" />
              <span className="font-semibold text-amber-800 dark:text-amber-300 text-sm">Quick AI Recipe Generation:</span>
            </div>
            <div className="flex flex-wrap gap-2">
              {dietTypes.filter(d => d.id !== 'all').map((diet) => {
                const Icon = diet.icon;
                return (
                  <button
                    key={diet.id}
                    onClick={() => generateRecipeForDiet(diet.id)}
                    disabled={loading}
                    className={`flex items-center gap-2 px-3 py-2 rounded-lg ${diet.color} text-white hover:opacity-90 transition-all disabled:opacity-50 text-xs sm:text-sm`}
                    data-testid={`quick-generate-${diet.id}`}
                  >
                    <Icon size={14} />
                    <span className="hidden sm:inline">Generate</span> {diet.name}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Recipe Grid */}
          {filteredRecipes.length > 0 ? (
            <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
              {filteredRecipes.map((recipe, index) => (
                <div 
                  key={index} 
                  className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow cursor-pointer"
                  onClick={() => setCurrentRecipe(recipe)}
                  data-testid={`recipe-card-${index}`}
                >
                  <div className="h-32 bg-gradient-to-br from-green-400 to-emerald-500 flex items-center justify-center">
                    <ChefHat size={36} className="text-white" />
                  </div>
                  <div className="p-4">
                    <h3 className="font-bold text-base mb-2 line-clamp-1">{recipe.title || recipe.name}</h3>
                    <div className="flex flex-wrap gap-1 mb-3">
                      {(recipe.dietary_restrictions || []).slice(0, 2).map((tag, i) => (
                        <span key={i} className="text-xs px-2 py-1 bg-green-100 text-green-700 rounded-full">
                          {tag}
                        </span>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 text-xs text-gray-500 dark:text-gray-400">
                      <span className="flex items-center gap-1">
                        <Clock size={12} /> {recipe.prep_time || '30 min'}
                      </span>
                      <span className="flex items-center gap-1">
                        <Users size={12} /> {recipe.servings || 4}
                      </span>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <div className="text-center py-12 bg-gray-50 dark:bg-gray-900 dark:bg-gray-800 rounded-xl">
              <ChefHat size={40} className="mx-auto text-gray-300 mb-4" />
              <p className="text-gray-500 dark:text-gray-400 mb-4 text-sm">No recipes found for this diet type.</p>
              <button
                onClick={() => setActiveTab('generate')}
                className="bg-[#FF6600] text-white px-6 py-2 rounded-lg font-medium text-sm"
              >
                Generate Your First Recipe
              </button>
            </div>
          )}
        </div>
      )}

      {/* Generate Recipe Tab */}
      {activeTab === 'generate' && (
        <div className="bg-white dark:bg-gray-800 p-4 sm:p-6 rounded-xl shadow-lg space-y-6">
          <h3 className="text-lg font-bold flex items-center gap-2">
            <Sparkles className="text-[#FF6600]" />
            Generate AI Recipe
          </h3>

          {/* Ingredients Input */}
          <div>
            <label className="font-semibold block mb-2 text-sm">Your Ingredients (optional)</label>
            <div className="flex gap-2 mb-3">
              <input
                type="text"
                value={ingredientInput}
                onChange={(e) => setIngredientInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && addIngredient()}
                placeholder="Add an ingredient..."
                className="flex-1 px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 focus:border-[#FF6600] text-sm"
                data-testid="ingredient-input"
              />
              <button 
                onClick={addIngredient}
                className="px-4 py-2 border border-[#FF6600] text-[#FF6600] rounded-lg hover:bg-[#FF6600]/10"
              >
                <Plus size={18} />
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {recipeRequest.ingredients.map((ing, i) => (
                <span key={i} className="flex items-center gap-1 px-3 py-1 bg-green-100 text-green-700 rounded-full text-sm">
                  {ing}
                  <X size={12} className="cursor-pointer" onClick={() => removeIngredient(ing)} />
                </span>
              ))}
            </div>
          </div>

          {/* Cuisine Type */}
          <div>
            <label className="font-semibold block mb-2 text-sm">Cuisine Type</label>
            <select
              value={recipeRequest.cuisine_type}
              onChange={(e) => setRecipeRequest({...recipeRequest, cuisine_type: e.target.value})}
              className="w-full px-4 py-2 rounded-lg border border-gray-200 dark:border-gray-700 text-sm"
              data-testid="cuisine-select"
            >
              {cuisineOptions.map(cuisine => (
                <option key={cuisine} value={cuisine}>{cuisine === 'any' ? 'Any Cuisine' : cuisine}</option>
              ))}
            </select>
          </div>

          {/* Generate Buttons */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3">
            {dietTypes.filter(d => d.id !== 'all').map((diet) => {
              const Icon = diet.icon;
              return (
                <button
                  key={diet.id}
                  onClick={() => generateRecipeForDiet(diet.id)}
                  disabled={loading}
                  className={`flex flex-col items-center gap-2 p-4 rounded-xl ${diet.lightColor} hover:shadow-lg transition-all disabled:opacity-50`}
                  data-testid={`generate-${diet.id}`}
                >
                  <Icon size={24} className={diet.color.replace('bg-', 'text-')} />
                  <span className="font-medium text-xs">{diet.name}</span>
                </button>
              );
            })}
          </div>

          <button
            onClick={() => generateRecipeForDiet()}
            disabled={loading}
            className="w-full bg-[#FF6600] text-white py-3 rounded-lg font-semibold hover:bg-[#E55C00] disabled:opacity-50"
            data-testid="generate-recipe-btn"
          >
            {loading ? 'Generating...' : '✨ Generate Recipe'}
          </button>
        </div>
      )}

      {/* AI Nutritionist Tab */}
      {activeTab === 'nutritionist' && (
        <div className="bg-white dark:bg-gray-800 rounded-xl shadow-lg overflow-hidden">
          {/* Chat Header */}
          <div className="bg-gradient-to-r from-blue-500 to-indigo-600 p-4 text-white">
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full bg-white dark:bg-gray-800/20 flex items-center justify-center">
                <Bot size={20} />
              </div>
              <div>
                <h3 className="font-bold">AI Nutritionist</h3>
                <p className="text-xs opacity-90">Ask me about recipes, diets, and nutrition advice</p>
              </div>
            </div>
          </div>

          {/* Quick Prompts */}
          <div className="p-3 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 border-b flex flex-wrap gap-2">
            {quickChatPrompts.map((prompt, i) => (
              <button
                key={i}
                onClick={() => setChatInput(prompt)}
                className="text-xs px-3 py-1 bg-white dark:bg-gray-600 border border-gray-200 dark:border-gray-700 dark:border-gray-500 rounded-full hover:bg-gray-100 dark:hover:bg-gray-500 transition-colors"
              >
                {prompt}
              </button>
            ))}
          </div>

          {/* Chat Messages */}
          <div className="h-64 sm:h-80 overflow-y-auto p-4 space-y-4" data-testid="chat-messages">
            {chatMessages.length === 0 && (
              <div className="text-center py-8 text-gray-500 dark:text-gray-400">
                <Bot size={36} className="mx-auto mb-4 opacity-50" />
                <p className="text-sm">Ask me anything about nutrition, diets, or recipe recommendations!</p>
              </div>
            )}
            {chatMessages.map((msg, i) => (
              <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] p-3 rounded-2xl text-sm ${
                  msg.role === 'user' 
                    ? 'bg-[#FF6600] text-white rounded-br-none' 
                    : 'bg-gray-100 dark:bg-gray-700 text-gray-800 dark:text-gray-100 dark:text-gray-200 rounded-bl-none'
                }`}>
                  <p className="whitespace-pre-wrap">{msg.content}</p>
                </div>
              </div>
            ))}
            {chatLoading && (
              <div className="flex justify-start">
                <div className="bg-gray-100 dark:bg-gray-700 p-3 rounded-2xl rounded-bl-none">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.1s'}} />
                    <div className="w-2 h-2 bg-gray-400 rounded-full animate-bounce" style={{animationDelay: '0.2s'}} />
                  </div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          {/* Chat Input */}
          <div className="p-3 border-t dark:border-gray-700 bg-white dark:bg-gray-800">
            <div className="flex gap-2">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Ask about recipes, diets, nutrition..."
                className="flex-1 px-4 py-2 rounded-full border border-gray-200 dark:border-gray-700 dark:border-gray-600 focus:border-[#FF6600] focus:ring-2 focus:ring-[#FF6600]/20 text-sm dark:bg-gray-700"
                data-testid="chat-input"
              />
              <button
                onClick={sendChatMessage}
                disabled={chatLoading || !chatInput.trim()}
                className="w-10 h-10 rounded-full bg-[#FF6600] text-white flex items-center justify-center hover:bg-[#E55C00] disabled:opacity-50"
                data-testid="send-message-btn"
              >
                <Send size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Recipe Detail Modal */}
      {currentRecipe && (
        <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-4" onClick={() => { setCurrentRecipe(null); setVideoStatus(null); setVideoJobId(null); setVideoGenerating(false); }}>
          <div className="bg-white dark:bg-gray-800 rounded-2xl max-w-2xl w-full max-h-[90vh] overflow-y-auto" onClick={e => e.stopPropagation()}>
            <div className="p-4 sm:p-6">
              <div className="flex justify-between items-start mb-4">
                <h2 className="text-xl font-bold pr-4">{currentRecipe.title || currentRecipe.name}</h2>
                <button onClick={() => { setCurrentRecipe(null); setVideoStatus(null); setVideoJobId(null); setVideoGenerating(false); }} className="p-2 hover:bg-gray-100 dark:hover:bg-gray-700 rounded-full flex-shrink-0">
                  <X size={20} />
                </button>
              </div>
              
              <div className="flex flex-wrap gap-2 mb-4">
                {(currentRecipe.dietary_restrictions || []).map((tag, i) => (
                  <span key={i} className="px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs">
                    {tag}
                  </span>
                ))}
              </div>

              <div className="flex flex-wrap gap-4 mb-6 text-gray-600 dark:text-gray-300 text-sm">
                <span className="flex items-center gap-1"><Clock size={16} /> {currentRecipe.prep_time || '30 min'}</span>
                <span className="flex items-center gap-1"><Users size={16} /> {currentRecipe.servings || 4} servings</span>
                <span className="flex items-center gap-1"><Star size={16} /> {currentRecipe.calories || 'N/A'} cal</span>
              </div>

              <div className="space-y-5">
                <div>
                  <h4 className="font-bold mb-2 text-sm">Ingredients:</h4>
                  <ul className="list-disc list-inside space-y-1 text-gray-700 dark:text-gray-300 text-sm">
                    {(currentRecipe.ingredients || []).map((ing, i) => (
                      <li key={i}>{ing}</li>
                    ))}
                  </ul>
                </div>

                <div>
                  <h4 className="font-bold mb-2 text-sm">Instructions:</h4>
                  <ol className="list-decimal list-inside space-y-2 text-gray-700 dark:text-gray-300 text-sm">
                    {(currentRecipe.instructions || []).map((step, i) => (
                      <li key={i}>{step}</li>
                    ))}
                  </ol>
                </div>

                {currentRecipe.nutrition && (
                  <div>
                    <h4 className="font-bold mb-2 text-sm">Nutrition Facts:</h4>
                    <div className="grid grid-cols-4 gap-2">
                      <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
                        <div className="font-bold text-sm">{currentRecipe.nutrition.calories || 'N/A'}</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Cal</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
                        <div className="font-bold text-sm">{currentRecipe.nutrition.protein || 'N/A'}g</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Protein</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
                        <div className="font-bold text-sm">{currentRecipe.nutrition.carbs || 'N/A'}g</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Carbs</div>
                      </div>
                      <div className="text-center p-2 bg-gray-50 dark:bg-gray-900 dark:bg-gray-700 rounded-lg">
                        <div className="font-bold text-sm">{currentRecipe.nutrition.fat || 'N/A'}g</div>
                        <div className="text-xs text-gray-500 dark:text-gray-400">Fat</div>
                      </div>
                    </div>
                  </div>
                )}
              </div>

              <button
                onClick={() => askNutritionistForRecipe(activeDiet !== 'all' ? activeDiet : 'healthy')}
                className="w-full mt-6 py-2 border-2 border-[#FF6600] text-[#FF6600] rounded-lg font-medium flex items-center justify-center gap-2 hover:bg-[#FF6600]/10"
              >
                <MessageCircle size={18} />
                Ask AI Nutritionist About This
              </button>

              {/* Generate Recipe Video */}
              <button
                onClick={() => generateRecipeVideo(currentRecipe)}
                disabled={videoGenerating}
                className="w-full mt-3 py-2.5 bg-gradient-to-r from-purple-600 to-pink-600 hover:from-purple-700 hover:to-pink-700 disabled:opacity-50 text-white rounded-lg font-medium flex items-center justify-center gap-2 transition-all"
                data-testid="generate-recipe-video-btn"
              >
                {videoGenerating ? (
                  <><Loader2 size={18} className="animate-spin" /> Generating Video...</>
                ) : (
                  <><Video size={18} /> Generate Cooking Video</>
                )}
              </button>

              {/* Video Status */}
              {videoStatus && (
                <div className={`mt-3 p-3 rounded-lg text-sm ${
                  videoStatus.status === 'completed' ? 'bg-green-50 dark:bg-green-900/20 border border-green-200 dark:border-green-800' :
                  videoStatus.status === 'failed' ? 'bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800' :
                  'bg-purple-50 dark:bg-purple-900/20 border border-purple-200 dark:border-purple-800'
                }`} data-testid="recipe-video-status">
                  {videoStatus.status === 'processing' && (
                    <div className="flex items-center gap-2 text-purple-700 dark:text-purple-300">
                      <Loader2 size={16} className="animate-spin" />
                      <span>Sora 2 is creating your cooking video... This takes 2-5 minutes.</span>
                    </div>
                  )}
                  {videoStatus.status === 'completed' && (
                    <div className="flex items-center justify-between">
                      <span className="text-green-700 dark:text-green-300 flex items-center gap-2">
                        <Video size={16} /> Video ready!
                      </span>
                      <a
                        href={`${API}/video/download/${videoJobId}?token=${token}`}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="flex items-center gap-1 text-xs bg-green-600 hover:bg-green-700 text-white px-3 py-1.5 rounded-lg transition-colors"
                        data-testid="download-recipe-video-btn"
                      >
                        <Download size={14} /> Download
                      </a>
                    </div>
                  )}
                  {videoStatus.status === 'failed' && (
                    <span className="text-red-600 dark:text-red-400">{videoStatus.error || 'Video generation failed'}</span>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default DietRecipesHub;
